import { RETAINED_RECORDS, type RetainedRecordKind } from '@kortex/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Empty, ErrorState, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';

/**
 * The erasure ledger: every record whose personal data was erased, how far,
 * and why - and which records kept under law still hold some of it. The
 * answer to "show us that this guest's data was erased".
 */

interface Entry {
  id: string;
  entity: string;
  entityId: string;
  outcome: 'ANONYMISED' | 'ERASED_EXCEPT_RETAINED' | 'DELETED';
  fieldsCleared: string[];
  retained: Array<{ kind: RetainedRecordKind; count: number }>;
  trigger: string;
  requestId: string | null;
  processedAt: string;
}

const ENTITY_LABELS: Record<string, string> = {
  Guest: 'Guest',
  RoomBooking: 'Online booking',
  Reservation: 'Table reservation',
  Order: 'Order customer',
  Employee: 'Former employee',
  User: 'Staff account',
  DataPrincipalRequest: 'Data request contact',
};

const TRIGGER_LABELS: Record<string, string> = {
  ERASURE_REQUEST: 'Erasure',
  RETENTION: 'Retention policy',
  ACCOUNT_CLOSURE: 'Account closure',
  FINAL_PURGE: 'Final purge',
  RESTORE_REPLAY: 'Re-applied after a restore',
  BACKFILLED: 'Before this log existed',
};

const PAGE = 50;

export function ErasureLogTab() {
  const [offset, setOffset] = useState(0);
  const log = useQuery({
    queryKey: ['privacy-erasures', offset],
    queryFn: () => api.get<{ total: number; entries: Entry[] }>(`/api/privacy/erasures?limit=${PAGE}&offset=${offset}`),
  });
  if (log.isLoading) return <Loading />;
  if (log.error) return <ErrorState error={log.error} onRetry={() => void log.refetch()} />;
  const { total, entries } = log.data!;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Erasure log</h3>
      </div>
      <div className="card-body small muted">
        <b>Anonymised</b>: nothing kept can be tied back to the person. <b>Erased except records kept under law</b>:
        their details are gone, but a record the law may require you to keep - an issued tax invoice with the name and
        GSTIN printed on it, payslips under an employee code - still holds some of them, for as long as that law
        requires. Record ids only; no personal values are kept here.
      </div>
      {entries.length === 0 ? (
        <Empty message="Nothing has been erased yet" />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>When</th>
                <th>Record</th>
                <th>Result</th>
                <th>Cleared</th>
                <th>Still held by</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{dateTime(entry.processedAt)}</td>
                  <td>
                    {ENTITY_LABELS[entry.entity] ?? entry.entity}
                    <div className="xs muted mono">{entry.entityId.slice(0, 8)}</div>
                  </td>
                  <td>
                    {entry.outcome === 'ANONYMISED' ? (
                      <span className="chip ok">Anonymised</span>
                    ) : entry.outcome === 'DELETED' ? (
                      <span className="chip">Deleted</span>
                    ) : (
                      <span className="chip warn">Erased except records kept under law</span>
                    )}
                  </td>
                  <td className="xs">{entry.fieldsCleared.join(', ')}</td>
                  <td className="xs">
                    {entry.retained.length === 0
                      ? '—'
                      : entry.retained.map((item) => (
                          <div key={item.kind} title={RETAINED_RECORDS[item.kind]?.basis}>
                            {RETAINED_RECORDS[item.kind]?.label ?? item.kind} ({item.count})
                          </div>
                        ))}
                  </td>
                  <td className="xs">{TRIGGER_LABELS[entry.trigger] ?? entry.trigger}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > PAGE ? (
        <div className="card-body row between">
          <span className="small muted">
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
          </span>
          <div className="row gap-6">
            <button className="btn sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              Previous
            </button>
            <button className="btn sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
